import ReactDOM from 'react-dom';
import { RouterProvider } from 'react-router-dom';

// third-party
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

// scroll bar
import 'simplebar/src/simplebar.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// apex-chart
import 'assets/third-party/apex-chart.css';
import 'assets/third-party/react-table.css';
import 'assets/third-party/full-calendar.css';

// project import
import { store, persister } from 'store';
import { ConfigProvider } from 'contexts/ConfigContext';
import reportWebVitals from './reportWebVitals';
import DOMProtection from 'components/DOMProtection';

import 'flag-icons/css/flag-icons.min.css';
import { config } from './env';
import AppBaseRouter from 'AppBaseRouter';
import { initOaiq } from 'utils/oaiq';
import { analytics } from 'lib/analytics';
import { rememberDeepLink } from 'features/announcements/deepLink';
import { trackServerCalls } from 'lib/prompts';

// ==============================|| GLOBAL DOM ERROR HANDLER ||============================== //
// Suppress DOM manipulation errors from third-party scripts (Crisp, Google Translate, extensions)
// These errors occur when external scripts modify DOM that React is managing
const originalConsoleError = console.error;
console.error = (...args: any[]) => {
  const errorMessage = args[0]?.toString?.() || '';
  const suppressPatterns = [
    'removeChild',
    'insertBefore',
    'appendChild',
    'replaceChild',
    'not a child of this node',
    'not a sub-node of this node'
  ];

  const shouldSuppress = suppressPatterns.some((pattern) => errorMessage.toLowerCase().includes(pattern.toLowerCase()));

  if (!shouldSuppress) {
    originalConsoleError.apply(console, args);
  }
};

// ==============================|| STORAGE VERSION CHECK ||============================== //
// Increment this version when you deploy changes that require clearing user storage
const STORAGE_VERSION = config.storageVersion;
const STORAGE_VERSION_KEY = 'APP_STORAGE_VERSION';

const storedVersion = localStorage.getItem(STORAGE_VERSION_KEY);
if (storedVersion !== STORAGE_VERSION) {
  // Clear all storage
  localStorage.clear();
  sessionStorage.clear();

  // Clear IndexedDB databases
  if (window.indexedDB && window.indexedDB.databases) {
    window.indexedDB.databases().then((databases) => {
      databases.forEach((db) => {
        if (db.name) window.indexedDB.deleteDatabase(db.name);
      });
    });
  }

  // Set the new version
  localStorage.setItem(STORAGE_VERSION_KEY, STORAGE_VERSION);

  // Reload to ensure clean state
  window.location.reload();
}

// ==============================|| ANALYTICS INIT ||============================== //
// Placed AFTER the storage-version block above because that block does a
// localStorage.clear() + reload, which wipes the oaiq conversion-dedup ledger
// (tc.oaiq.fired.v1) and PostHog's persisted distinct_id. Initialising here
// means init runs on the post-reload load path rather than on the doomed one,
// while still booting before React so the earliest events are not gated on
// render. Losing the ledger on a storageVersion bump is tolerable — OpenAI
// dedupes on event_id server-side. Both are no-ops when no key is configured.
initOaiq();
analytics.init();
// A What's New link from a launch email must survive the sign-in redirect.
rememberDeepLink(window.location.search);
// Prompts wait for a page's server calls to finish, so every call is counted from the first.
trackServerCalls();

// ==============================|| MAIN - REACT DOM RENDER  ||============================== //

if (config.env_name === 'prod') {
  console.log = () => {};
  console.debug = () => {};
  console.warn = () => {};
}

ReactDOM.render(
  <DOMProtection>
    <ReduxProvider store={store}>
      <PersistGate loading={null} persistor={persister}>
        <ConfigProvider>
          <RouterProvider router={AppBaseRouter}></RouterProvider>
          {/* <BrowserRouter>
            <App />
          </BrowserRouter> */}
        </ConfigProvider>
      </PersistGate>
    </ReduxProvider>
  </DOMProtection>,
  document.getElementById('root')
);

// If you want to start measuring performance in your app, pass a function
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
